import { Button } from '@thanx/uikit/button'
import { Notification } from '@thanx/uikit/notification'
import { Text } from '@thanx/uikit/text'
import { buildTranslate } from 'locales'
import React, { useCallback, useRef } from 'react'
import { useStyletron } from 'styletron-react'
import { merchantTheme } from 'theme'
import {
  ACCEPTED_BRAND_VOICE_EXTENSIONS,
  BrandVoiceFileError,
} from './brandVoiceUpload'

const t = buildTranslate('recovery_ai.brand_voice')

const SECTION_GAP = '16px'
const CHIP_GAP = '8px'
const DROPZONE_GAP = '8px'
const DROPZONE_MIN_HEIGHT = '160px'
const DROPZONE_PADDING = '24px'

type Props = {
  files: File[]
  onAddFiles: (files: FileList | null) => void
  onRemoveFile: (index: number) => void
  clientError: BrandVoiceFileError | null
}

// Chrome-free, controlled "Generate from documents" sub-view (states 2/3, W7).
// Presentational only — no fetch, no useDispatch: the host owns the staged
// files, validation, and the derive call. The chip row maps over an ARRAY so
// lifting the single-file v1 cap later is a one-line change with no re-layout.
// Drag-and-drop is a mouse-only enhancement; the keyboard/AT path is the browse
// Button (the zone itself takes no role because that Button is its AT control).
const BrandVoiceUploadView = ({
  files,
  onAddFiles,
  onRemoveFile,
  clientError,
}: Props) => {
  const [css] = useStyletron()
  const inputRef = useRef<HTMLInputElement>(null)

  const openPicker = useCallback(() => inputRef.current?.click(), [])

  const handleDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault()
      onAddFiles(event.dataTransfer.files)
    },
    [onAddFiles]
  )

  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault()
  }, [])

  const errorMessage = ((): string | null => {
    if (clientError === 'type') return t('upload_error_type')
    if (clientError === 'size') return t('upload_error_size')
    return null
  })()

  return (
    <div
      className={css({
        display: 'flex',
        flexDirection: 'column',
        gap: SECTION_GAP,
      })}
    >
      <Text.BodyText3 color="grey70">{t('upload_intro')}</Text.BodyText3>

      {files.length > 0 && (
        <div
          className={css({ display: 'flex', flexWrap: 'wrap', gap: CHIP_GAP })}
        >
          {files.map((file, index) => (
            <div
              // Never the array index — key on a stable per-file value.
              key={`${file.name}-${file.size}`}
              className={css({
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                paddingLeft: '8px',
                borderRadius: '4px',
                borderStyle: 'solid',
                borderWidth: '1px',
                borderColor: merchantTheme.colors.grey40,
              })}
            >
              <i className="fa fa-file-text-o" aria-hidden="true" />
              <Text.BodyText4>{file.name}</Text.BodyText4>
              <Button
                kind="minimal"
                onClick={() => onRemoveFile(index)}
                aria-label={t('remove_file', { filename: file.name })}
              >
                <i className="fa fa-times" aria-hidden="true" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div
        data-testid="brand-voice-dropzone"
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        className={css({
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: DROPZONE_GAP,
          minHeight: DROPZONE_MIN_HEIGHT,
          padding: DROPZONE_PADDING,
          borderRadius: '4px',
          borderStyle: 'dashed',
          borderWidth: '1px',
          borderColor: merchantTheme.colors.grey50,
          backgroundColor: 'white',
        })}
      >
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED_BRAND_VOICE_EXTENSIONS.join(',')}
          data-testid="brand-voice-upload-input"
          // `.hidden` (display:none !important), not css({display:'none'}): the
          // global `input[type="file"] { display: block }` reset out-specifies a
          // styletron atomic class, so the styletron rule loses and the native
          // control would show. Matches the shipped upload card.
          className="hidden"
          // Clear the value after handling so re-picking the SAME file re-fires
          // change (the native input only fires on a value change). The File is
          // already captured by onAddFiles, so clearing is safe.
          onChange={event => {
            onAddFiles(event.target.files)
            event.target.value = ''
          }}
        />
        <i className="fa fa-upload fa-2x" aria-hidden="true" />
        {/* Minimal Button (not a plain heading) so the affordance is keyboard-
            reachable while reading as the mock's plain bold title. */}
        <Button kind="minimal" onClick={openPicker}>
          {t('upload_dropzone_title')}
        </Button>
        <Text.BodyText4 color="grey70">{t('upload_hint')}</Text.BodyText4>
      </div>

      {/* uikit Notification carries its own role="alert" (time-sensitive → AT
          interrupts); no extra wrapper role, or the alert doubles. */}
      {errorMessage && (
        <Notification kind="negative" size="full">
          {errorMessage}
        </Notification>
      )}
    </div>
  )
}

export default BrandVoiceUploadView
